
import { 
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from "firebase/auth";

export const signInWithEmailPassword = async (email: string, password: string) => {
  if (import.meta.env.VITE_USE_EMULATORS !== 'true') {
    throw new Error("signInWithEmailPassword is not supported.");
  }
  const { signInWithEmailAndPassword } = await import("firebase/auth");
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
};
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "./config";

const googleProvider = new GoogleAuthProvider();

export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    console.log("Google sign-in successful for:", user.email);
    
    // First check if user exists by Firebase auth ID (existing linked user)
    const { getUserById } = await import("./users");
    const userDocByAuthId = await getUserById(user.uid);
    
    if (userDocByAuthId) {
      console.log("Found existing user by auth ID");
      return { isNewUser: false, needsLinking: false, ...userDocByAuthId };
    }
    
    // If not found by auth ID, this could be a new user or needs linking
    console.log("No existing user found, checking if linking is needed");
    return { 
      isNewUser: true, 
      needsLinking: true,
      uid: user.uid, 
      email: user.email || "", 
      displayName: user.displayName || "",
      photoURL: user.photoURL || ""
    };
  } catch (error) {
    console.error("Error signing in with Google: ", error);
    throw error;
  }
};

// Separate function to attempt linking after authentication
export const attemptLinking = async (invitationCode: string) => {
  try {
    // Use onAuthStateChanged to wait for authenticated user
    const user = await new Promise<FirebaseUser | null>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error("Authentication timeout"));
      }, 10000); // 10 second timeout

      const unsubscribe = onAuthStateChanged(auth, (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        resolve(user);
      });
    });
    
    if (!user) {
      throw new Error("User not authenticated");
    }

    console.log("User authenticated via onAuthStateChanged:", user.email);
    
    // Force refresh the ID token to ensure it's valid and current
    const idToken = await user.getIdToken(true);
    console.log("Got fresh ID token, length:", idToken.length);

    // Cloud Functions - create the callable function
    const linkTemporaryUser = httpsCallable(functions, 'linkTemporaryUser');

    console.log("Calling cloud function with invitation code:", invitationCode);
    console.log("Current user UID:", user.uid);
    console.log("Current user email:", user.email);
    console.log("ID token exists:", !!idToken);
    
    // Call the function with invitation code
    const linkResult = await linkTemporaryUser({ invitationCode });
    
    console.log("Cloud function result:", linkResult.data);
    return { success: true, message: (linkResult.data as any)?.message || "Linking successful" };
  } catch (error) {
    console.error("Linking error details:", error);
    console.log("Current auth state:", auth.currentUser);
    return { success: false, error };
  }
};

export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error("Error signing out: ", error);
    throw error;
  }
};

export const getCurrentUser = (): Promise<FirebaseUser | null> => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};
